import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subscription } from 'rxjs';
import { DateManager } from 'src/app/core/managers/date.manager';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { ResponseList } from 'src/app/core/interfaces/response-list';
import { SelectableItem } from 'src/app/core/interfaces/selectableItem';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { Utils } from 'src/app/core/resources/utils';

@Component({
  selector: 'app-cargo-operation-by-plate',
  templateUrl: './cargo-operation-by-plate.component.html',
  styleUrls: ['./cargo-operation-by-plate.component.scss']
})
export class CargoOperationByPlateComponent implements OnInit, OnDestroy {
  private readonly PENDING = 'Pending approval';
  private readonly APPROVED = 'Approved';
  private readonly REJECTED = 'Rejected';
  private readonly pageSize = 500;

  licensePlate = new FormControl('', [Validators.required, Validators.minLength(4), Validators.maxLength(7)]);
  finishDate = new FormControl(null);
  scope = new FormControl('pending', Validators.required);
  rejectionReason = new FormControl('');

  scopeOptions: {
    value: string;
    label: string;
    states: string[];
  }[] = [];
  availableList: Cargo[] = [];
  unavailableList: (Cargo & { errorMessage?: string })[] = [];
  totalFound = 0;
  searched = false;

  private subscriptions = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<CargoOperationByPlateComponent>,
    public spinner: NgxSpinnerService,
    public snackBarService: SnackBarService,
    public utils: Utils,
    @Inject(MAT_DIALOG_DATA) public data: {
      mode: 'approve' | 'reject' | 'reverse';
      typeList: string;
      baseFilters: string;
      searchMethod: (filters: string, pageKey: number, pageSize: number, typeList: string) => Observable<ResponseList<Cargo>>;
      validationMethod: (cargo: Cargo) => SelectableItem;
    }
  ) { }

  ngOnInit(): void {
    if (this.data.mode === 'approve') {
      this.scopeOptions = [
        { value: 'pending', label: 'Pendientes', states: [this.PENDING] },
        { value: 'rejected', label: 'Rechazados', states: [this.REJECTED] },
        { value: 'pendingRejected', label: 'Pendientes y rechazados', states: [this.PENDING, this.REJECTED] }
      ];
      this.scope.setValue('pending');
    } else if (this.data.mode === 'reject') {
      this.scopeOptions = [
        { value: 'pending', label: 'Pendientes', states: [this.PENDING] },
        { value: 'approved', label: 'Aprobados', states: [this.APPROVED] },
        { value: 'pendingApproved', label: 'Pendientes y aprobados', states: [this.PENDING, this.APPROVED] }
      ];
      this.scope.setValue('pending');
    } else {
      this.scopeOptions = [
        { value: 'approved', label: 'Aprobados', states: [this.APPROVED] },
        { value: 'rejected', label: 'Rechazados', states: [this.REJECTED] },
        { value: 'approvedRejected', label: 'Aprobados y rechazados', states: [this.APPROVED, this.REJECTED] }
      ];
      this.scope.setValue('approved');
    }
    // El alcance define si la fecha es obligatoria; cualquier cambio invalida los resultados para forzar una nueva búsqueda.
    this.subscriptions.add(this.scope.valueChanges.subscribe(() => {
      this.updateFinishDateValidators();
      this.resetResults();
    }));
    [this.licensePlate, this.finishDate, this.rejectionReason].forEach(control => {
      this.subscriptions.add(control.valueChanges.subscribe(() => this.resetResults()));
    });
    this.updateFinishDateValidators();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get actionLabel(): string {
    switch (this.data.mode) {
      case 'approve': return 'aprobar';
      case 'reject': return 'rechazar';
      default: return 'reversar';
    }
  }

  get title(): string {
    switch (this.data.mode) {
      case 'approve': return 'Aprobar servicios por placa';
      case 'reject': return 'Rechazar servicios por placa';
      default: return 'Reversar servicios por placa';
    }
  }

  get selectedStates(): string[] {
    const option = this.scopeOptions.find(scope => scope.value === this.scope.value);
    return option ? option.states : [];
  }

  get includesRejected(): boolean {
    return this.selectedStates.includes(this.REJECTED);
  }
  get includesApproved(): boolean {
    return this.selectedStates.includes(this.APPROVED);
  }

  private updateFinishDateValidators(): void {
    if (this.includesApproved) this.finishDate.setValidators([Validators.required]);
    else this.finishDate.clearValidators();
    this.finishDate.updateValueAndValidity({ emitEvent: false });
  }

  get canContinue(): boolean {
    return this.searched && !!this.availableList.length;
  }

  getConsecutives(cargos: Cargo[]): string {
    return cargos.map(cargo => cargo.consecutive).join(', ');
  }

  async search(): Promise<void> {
    this.licensePlate.markAsTouched();
    if (this.licensePlate.invalid)
      return this.snackBarService.openSnackBar('Ingrese una placa válida (entre 4 y 7 caracteres)', undefined, 'alert');
    if (this.includesApproved && !this.finishDate.value)
      return this.snackBarService.openSnackBar('Ingrese una fecha para buscar servicios aprobados', undefined, 'alert');
    this.resetResults();
    this.spinner.show();
    try {
      const cargos = await this.fetchCargos(this.selectedStates);
      this.classify(cargos);
      this.totalFound = cargos.length;
      this.searched = true;
    } catch (error) {
      this.snackBarService.openSnackBar('No fue posible consultar los servicios de la placa', undefined, 'error');
    } finally {
      this.spinner.hide();
    }
  }

  continue(): void {
    if (!this.canContinue)
      return this.snackBarService.openSnackBar('No hay servicios disponibles para continuar', undefined, 'alert');
    this.dialogRef.close({ availableList: this.availableList });
  }

  private resetResults(): void {
    this.searched = false;
    this.availableList = [];
    this.unavailableList = [];
    this.totalFound = 0;
  }

  private async fetchCargos(states: string[]): Promise<Cargo[]> {
    const cargosById = new Map<string, Cargo>();
    for (const state of states) {
      const cargos = await this.fetchCargosByState(state);
      cargos.forEach(cargo => cargosById.set(cargo.id || `${cargo.consecutive}`, cargo));
    }
    return Array.from(cargosById.values());
  }

  private async fetchCargosByState(approvalState: string): Promise<Cargo[]> {
    const filters = this.buildFilters(approvalState);
    const cargos: Cargo[] = [];
    let pageKey = 1;
    let total = 0;
    let pageCargos: Cargo[] = [];
    do {
      const response = await this.data.searchMethod(filters, pageKey, this.pageSize, this.data.typeList).toPromise();
      pageCargos = response && response.results ? response.results : [];
      cargos.push(...pageCargos);
      total = response && response.pagination && this.utils.isDefined(response.pagination.total)
        ? response.pagination.total
        : cargos.length;
      pageKey++;
    } while (pageCargos.length && cargos.length < total);
    return cargos;
  }

  private buildFilters(approvalState: string): string {
    const excludedKeys = ['approval', 'licensePlate', 'dateLoad', 'date', 'endDate'];
    const filters = (this.data.baseFilters || '')
      .split('&')
      .filter(filter => filter && !excludedKeys.includes(filter.split('=')[0]));
    filters.push(`licensePlate=${encodeURIComponent((this.licensePlate.value || '').trim())}`);
    filters.push(`approval=${encodeURIComponent(approvalState)}`);
    if (this.finishDate.value) {
      const formattedDate = DateManager.formatDate(this.finishDate.value, null, 'YYYY-MM-DD ZZ');
      if (formattedDate) filters.push(`dateLoad=${encodeURIComponent(formattedDate)}`);
    }
    return `&${filters.join('&')}`;
  }

  private classify(cargos: Cargo[]): void {
    const reason = (this.rejectionReason.value || '').trim().toLowerCase();
    cargos.forEach(cargo => {
      const validation = this.data.validationMethod(cargo);
      let isAvailable = !!(validation.isSelectable && validation.isEnabled);
      let errorMessage = validation.errorMessage;
      if (isAvailable && this.includesRejected && cargo.approval === this.REJECTED &&
        (reason && (!cargo.reasonApproval || cargo.reasonApproval.trim().toLowerCase() !== reason))) {
        isAvailable = false;
        errorMessage = 'El motivo de rechazo no coincide';
      }
      if (isAvailable) this.availableList.push(cargo);
      else this.unavailableList.push({ ...cargo, errorMessage });
    });
  }
}
